/**
 * @fileoverview Hook for managing document received status in the Documents step.
 *
 * Handles section status tracking for retainer and HIPAA documents:
 * - 'received' = section status is 'found'
 * - 'not_received' = section status is 'not_found'
 * - null = not yet reviewed (default state)
 *
 * Section status updates are persisted via the case ingestion audit record.
 * Task completion is handled separately through the normal task management UI.
 */
import { useCallback, useMemo } from 'react';

import type { IngestionSections } from '@/services/types/case-ingestion-types';

import { useStepSave } from './useStepSave';

export type DocumentReceivedStatus = 'received' | 'not_received' | null;

interface UseDocumentTaskCompletionResult {
  retainerStatus: DocumentReceivedStatus;
  hipaaStatus: DocumentReceivedStatus;
  handleReceived: (type: 'retainer' | 'hipaa') => Promise<void>;
  handleNotReceived: (type: 'retainer' | 'hipaa') => Promise<void>;
  isLoading: boolean;
}

/**
 * Derive document received status from section status.
 */
function deriveStatus(
  sectionStatus: 'found' | 'not_found' | null,
): DocumentReceivedStatus {
  if (sectionStatus === 'found') {
    return 'received';
  }
  if (sectionStatus === 'not_found') {
    return 'not_received';
  }
  return null;
}

/**
 * Map document type to section key.
 */
function getSectionKey(type: 'retainer' | 'hipaa') {
  return type === 'retainer' ? 'retainer_document' : 'hipaa_document';
}

/**
 * Hook for managing document received status in the Documents step.
 *
 * Updates section statuses in the case ingestion audit record:
 * - "Received" marks section as 'found'
 * - "Not Received" marks section as 'not_found'
 */
export function useDocumentTaskCompletion(
  _matterId: string,
  sectionStatuses: IngestionSections,
): UseDocumentTaskCompletionResult {
  const { markSection, isSaving } = useStepSave();

  // Derive statuses from section data
  const retainerStatus = useMemo(
    () => deriveStatus(sectionStatuses.retainer_document),
    [sectionStatuses.retainer_document],
  );

  const hipaaStatus = useMemo(
    () => deriveStatus(sectionStatuses.hipaa_document),
    [sectionStatuses.hipaa_document],
  );

  // Handle "Received" click - marks section as 'found'
  const handleReceived = useCallback(
    async (type: 'retainer' | 'hipaa') => {
      const sectionKey = getSectionKey(type);
      const displayName = type === 'retainer' ? 'Retainer' : 'HIPAA';

      await markSection(sectionKey, 'found', {
        successMessage: `${displayName} marked as received`,
      });
    },
    [markSection],
  );

  // Handle "Not Received" click - marks section as 'not_found'
  const handleNotReceived = useCallback(
    async (type: 'retainer' | 'hipaa') => {
      const sectionKey = getSectionKey(type);
      const displayName = type === 'retainer' ? 'Retainer' : 'HIPAA';

      await markSection(sectionKey, 'not_found', {
        successMessage: `${displayName} marked as not received`,
      });
    },
    [markSection],
  );

  return {
    retainerStatus,
    hipaaStatus,
    handleReceived,
    handleNotReceived,
    isLoading: isSaving,
  };
}
